import { memo, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useHelpContent } from '../lib/help-source.ts';
import { headingId, helpLink, helpUrl } from '../lib/help-links.ts';
import './help.css';

function Diagram({ source }: { source: string }) {
  const id = useId().replace(/[^a-zA-Z0-9]/g, '');
  const ref = useRef<HTMLDivElement>(null);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void import('mermaid').then(async ({ default: mermaid }) => {
      mermaid.initialize({ startOnLoad: false, securityLevel: 'strict', theme: 'dark', suppressErrorRendering: true });
      const { svg } = await mermaid.render(`help-diagram-${id}`, source);
      if (!cancelled && ref.current) ref.current.innerHTML = svg;
    }).catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [id, source]);
  return <div className="help-diagram">
    <button type="button" className="btn btn-sm" aria-pressed={expanded} onClick={() => setExpanded((value) => !value)}>
      {expanded ? 'Fit diagram' : 'Enlarge diagram'}
    </button>
    <div className="help-diagram-scroll"><div className={expanded ? 'is-expanded' : undefined} ref={ref} role="img" aria-label="Documentation diagram" /></div>
    {error ? <p role="status">Diagram could not be rendered. Its source is available below.</p> : null}
    <details><summary>Diagram source</summary><pre><code>{source}</code></pre></details>
  </div>;
}

function textContent(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textContent).join('');
  if (node && typeof node === 'object' && 'props' in node) return textContent((node.props as { children?: ReactNode }).children);
  return '';
}

export const HelpView = memo(function HelpView() {
  const { pages, assets, loading, error } = useHelpContent();
  const [location, setLocation] = useState(() => window.location.search + window.location.hash);
  const [query, setQuery] = useState('');
  const content = useRef<HTMLElement>(null);
  useEffect(() => {
    const changed = () => setLocation(window.location.search + window.location.hash);
    window.addEventListener('popstate', changed);
    window.addEventListener('hashchange', changed);
    return () => {
      window.removeEventListener('popstate', changed);
      window.removeEventListener('hashchange', changed);
    };
  }, []);
  const id = new URLSearchParams(location.split('#')[0]).get('doc') ?? 'docs/README.md';
  const page = pages.find((entry) => entry.id === id);
  const matches = pages.filter((entry) => `${entry.title} ${entry.id} ${entry.markdown}`.toLowerCase().includes(query.toLowerCase()));
  useEffect(() => {
    const fragment = location.split('#')[1];
    if (fragment) {
      try { document.getElementById(decodeURIComponent(fragment))?.scrollIntoView(); } catch { /* Malformed bookmark. */ }
    } else content.current?.scrollTo(0, 0);
  }, [location, page]);
  const ids = new Map<string, number>();
  const heading = (level: 1 | 2 | 3 | 4 | 5 | 6): Components['h1'] => ({ children }) => {
    const base = headingId(textContent(children));
    const count = ids.get(base) ?? 0;
    ids.set(base, count + 1);
    const Tag = `h${level}` as const;
    return <Tag id={count ? `${base}-${count}` : base}>{children}</Tag>;
  };
  const components: Components = {
    a: ({ href = '', children }) => {
      const target = helpLink(id, href, assets);
      // External pages and bundled images open in a new tab so the Help reader stays put.
      return <a href={target} {...(/^(https?:)?\/\//.test(target) || Object.values(assets).includes(target) ? { target: '_blank', rel: 'noreferrer' } : {})}>{children}</a>;
    },
    h1: heading(1), h2: heading(2), h3: heading(3), h4: heading(4), h5: heading(5), h6: heading(6),
    pre: ({ children }) => {
      const child = Array.isArray(children) ? children[0] : children;
      const diagram = child && typeof child === 'object' && 'props' in child
        && (child.props as { className?: string }).className === 'language-mermaid';
      return diagram ? <div className="help-code">{children}</div> : <pre>{children}</pre>;
    },
    code: ({ className, children }) => className === 'language-mermaid'
      ? <Diagram source={String(children)} />
      : <code className={className}>{children}</code>,
  };
  return <div className="help" onClick={(event) => {
    const link = (event.target as HTMLElement).closest<HTMLAnchorElement>('a');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href);
    if (url.origin !== window.location.origin || url.pathname !== '/help') return;
    event.preventDefault();
    window.history.pushState(null, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }}>
    <aside className="help-index" aria-label="Documentation index">
      <h2>Help</h2>
      <p>Learn and operate Nightshift</p>
      <a href={helpUrl('docs/README.md')}>Documentation home</a>
      <label htmlFor="help-search">Search documentation</label>
      <input id="help-search" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Topic or phrase" />
      <nav aria-label="Help pages">
        {matches.map((entry) => <a key={entry.id} href={helpUrl(entry.id)} aria-current={entry.id === id ? 'page' : undefined}>{entry.title}</a>)}
      </nav>
      {matches.length === 0 ? <p>No matching pages. Try another phrase.</p> : null}
    </aside>
    <article className="help-reader" ref={content} aria-label="Documentation page">
      <p className="help-source">Wiki · Read-only{page ? ` · ${page.id} · Updated ${new Date(page.updatedAt).toLocaleDateString()}` : ''}</p>
      {loading ? <p role="status">Loading documentation…</p> : error ? <p role="alert">{error}</p>
        : page ? <div className="md"><ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{page.markdown}</ReactMarkdown></div>
        : <><h2>Page not found</h2><p>This page is not included in this version of Help.</p><a href={helpUrl('docs/README.md')}>Return to documentation home</a></>}
    </article>
  </div>;
});
